import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ThreeColumnLayout } from 'sharedComponents/ThreeColumnLayout';
import HierarchyRightPanel from './HierarchyRightPanel';
import HierarchyLeftPanel from './HierarchyLeftPanel';
import { transformHierarchyToTree, generateAllNodeIds } from './helpers';
import { useHierarchyData } from 'sharedComponents/HierarchyView/HierarchyHooks/useHierarchyData';
import UnSavedChangesModal from '../../../PowertrainAssetDetails/DetailedInformation/LimitConfigurationBeta/components/UnSavedChangesModal';
import useUnsavedChanges from 'sharedHooks/useUnsavedChanges';
import './style.scss';

const HierarchyComponent = (props) => {

    const {
        selectedPowertrain,
        filteredPowertrains,
        searchboxFilter,
        sites,
        selectedSites,
        organizations,
        setSelectedPowertrain,
        deletePowertrainRequest,
        convertToLocationRequest,
        setPowertrainForEditing,
        setPowertrainWizardVisible,
        setWizardSelectedNode,
        hierarchyData,
        getHierarchyDataRequest,
        updateHierarchyDataRequest,
        selectedHierarchyNodes,
        setSelectedHierarchyNodes,
        hierarchyExpandedNodes,
        setHierarchyExpandedNodes,
        isEditHierarchy,
        setIsEditHierarchy,
        hierarchyRequestData,
        setHierarchyRequestData,
        hierarchySelectedAssets,
        setHierarchySelectedAssets,
        updateHierarchyLoadingStatus,
        hierarchyErrorModalVisible,
        isSaveFailed,
        setSaveFailed,
        isPartialPowertrainFailure,
        setPartialPowertrainFailure,
        setHierarchyErrorModal,
        conversionResult,
        clearConversionResult
    } = props;

    const windowWidth = window.innerWidth;
    const [baseHierarchyTree, setBaseHierarchyTree] = useState([]);
    const hasUnsavedChanges = isEditHierarchy && hierarchyRequestData && hierarchyRequestData.length > 0;
    const {
        showUnsavedModal,
        setShowUnsavedModal,
        handleDiscardAndContinue: originalHandleDiscardAndContinue
    } = useUnsavedChanges(hasUnsavedChanges);

    const initbaseTree = () => {
        const baseTree = transformHierarchyToTree([hierarchyData]);
        setBaseHierarchyTree(baseTree);
        setHierarchyExpandedNodes(generateAllNodeIds(baseTree));
    };

    const handleDiscardAndContinue = () => {
        setIsEditHierarchy(false);
        setHierarchyRequestData([]);
        initbaseTree();
        originalHandleDiscardAndContinue();
    };

    // Initialize base hierarchy tree
    useEffect(() => {
        initbaseTree();
    }, [hierarchyData]);

    // Clear conversion result banner when user navigates away from the converted location's single assets node
    useEffect(() => {
        if (conversionResult?.showBanner && selectedHierarchyNodes?.length > 0) {
            const expectedNodeId = `singleasset-location-${conversionResult.locationId}`;
            if (selectedHierarchyNodes[0] !== expectedNodeId) {
                clearConversionResult?.();
            }
        }
    }, [selectedHierarchyNodes, conversionResult?.locationId]);

    const { flattenedNodes, nodeMap } = useHierarchyData({
        nodes: baseHierarchyTree,
        expandedIds: hierarchyExpandedNodes,
        getNodeKey: (node) => node.id
    });

    return (
        <>
            {showUnsavedModal &&
                <UnSavedChangesModal
                    showModal={showUnsavedModal}
                    setShowModal={setShowUnsavedModal}
                    handleDiscardAndContinue={handleDiscardAndContinue}
                    isResetToDefault={false}
                />
            }
            <ThreeColumnLayout
                mainLayoutclass='configuration-management'
                leftPanelDefaulSize={10}
                leftPanelMinSize={windowWidth > 1400 ? 15 : 20}
                isLeftCollapsible={false}
                centerPanelDefaultSize={30}
                centerPanelMinSize={50}
                leftPanelHeader={null}
                isForHierarchyView={true}
            >
                <HierarchyLeftPanel
                    isEditHierarchy={isEditHierarchy}
                    setIsEditHierarchy={setIsEditHierarchy}
                    setPowertrainWizardVisible={setPowertrainWizardVisible}
                    setWizardSelectedNode={setWizardSelectedNode}
                    setSelectedPowertrain={setSelectedPowertrain}
                    updateHierarchyDataRequest={updateHierarchyDataRequest}
                    getHierarchyDataRequest={getHierarchyDataRequest}
                    organizations={organizations}
                    selectedIds={selectedHierarchyNodes}
                    setSelectedIds={setSelectedHierarchyNodes}
                    nodeMap={nodeMap}
                    baseHierarchyTree={baseHierarchyTree}
                    setBaseHierarchyTree={setBaseHierarchyTree}
                    expandedIds={hierarchyExpandedNodes}
                    setExpandedIds={setHierarchyExpandedNodes}
                    flattenedNodes={flattenedNodes}
                    hierarchyRequestData={hierarchyRequestData}
                    setHierarchyRequestData={setHierarchyRequestData}
                    filteredPowertrains={filteredPowertrains}
                    initbaseTree={initbaseTree}
                    updateHierarchyLoadingStatus={updateHierarchyLoadingStatus}
                    hierarchyErrorModalVisible={hierarchyErrorModalVisible}
                    isSaveFailed={isSaveFailed}
                    isPartialPowertrainFailure={isPartialPowertrainFailure}
                    setHierarchyErrorModal={setHierarchyErrorModal}
                    setSaveFailed={setSaveFailed}
                    setPartialPowertrainFailure={setPartialPowertrainFailure}
                    selectedSites={selectedSites}
                />
                {!isEditHierarchy &&
                    <HierarchyRightPanel
                        selectedPowertrain={selectedPowertrain}
                        searchboxFilter={searchboxFilter}
                        selectedSites={selectedSites}
                        sites={sites}
                        organizations={organizations}
                        deletePowertrainRequest={deletePowertrainRequest}
                        convertToLocationRequest={convertToLocationRequest}
                        setSelectedPowertrain={setSelectedPowertrain}
                        setPowertrainWizardVisible={setPowertrainWizardVisible}
                        setPowertrainForEditing={setPowertrainForEditing}
                        setWizardSelectedNode={setWizardSelectedNode}
                        selectedIds={selectedHierarchyNodes}
                        nodeMap={nodeMap}
                        updateHierarchyDataRequest={updateHierarchyDataRequest}
                        hierarchySelectedAssets={hierarchySelectedAssets}
                        setHierarchySelectedAssets={setHierarchySelectedAssets}
                        hierarchyData={baseHierarchyTree}
                        getHierarchyDataRequest={getHierarchyDataRequest}
                        setHierarchyErrorModal={setHierarchyErrorModal}
                        setSaveFailed={setSaveFailed}
                        conversionResult={conversionResult}
                        clearConversionResult={clearConversionResult}
                    />
                }
            </ThreeColumnLayout>
        </>
    );
};

HierarchyComponent.propTypes = {
    selectedPowertrain: PropTypes.object,
    filteredPowertrains: PropTypes.array,
    searchboxFilter: PropTypes.string,
    sites: PropTypes.array,
    selectedSites: PropTypes.array,
    organizations: PropTypes.array,
    setSelectedPowertrain: PropTypes.func,
    deletePowertrainRequest: PropTypes.func,
    convertToLocationRequest: PropTypes.func,
    setPowertrainForEditing: PropTypes.func,
    setPowertrainWizardVisible: PropTypes.func,
    setWizardSelectedNode: PropTypes.func,
    hierarchyData: PropTypes.object,
    getHierarchyDataRequest: PropTypes.func,
    updateHierarchyDataRequest: PropTypes.func,
    selectedHierarchyNodes: PropTypes.array,
    setSelectedHierarchyNodes: PropTypes.func,
    hierarchyExpandedNodes: PropTypes.array,
    setHierarchyExpandedNodes: PropTypes.func,
    isEditHierarchy: PropTypes.bool,
    setIsEditHierarchy: PropTypes.func,
    hierarchyRequestData: PropTypes.array,
    setHierarchyRequestData: PropTypes.func,
    hierarchySelectedAssets: PropTypes.array,
    setHierarchySelectedAssets: PropTypes.func,
    updateHierarchyLoadingStatus: PropTypes.string,
    hierarchyErrorModalVisible: PropTypes.bool,
    isSaveFailed: PropTypes.bool,
    setSaveFailed: PropTypes.func,
    isPartialPowertrainFailure: PropTypes.bool,
    setPartialPowertrainFailure: PropTypes.func,
    setHierarchyErrorModal: PropTypes.func,
    conversionResult: PropTypes.shape({
        siteId: PropTypes.number,
        locationId: PropTypes.number,
        locationName: PropTypes.string,
        showBanner: PropTypes.bool
    }),
    clearConversionResult: PropTypes.func
};

export default HierarchyComponent;
